import { IonButton, IonIcon } from "@ionic/react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { arrowForward, checkmarkCircle, closeOutline } from "ionicons/icons";
import type { Swiper as SwiperType } from "swiper";
import "swiper/css";
import "swiper/css/effect-creative";
import "swiper/css/pagination";
import { EffectCreative, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { Routes } from "@/lib/router";

interface OnboardingSlide {
  title: string;
  description: string;
  image?: string;
  icon: string;
  benefits?: string[];
  roles?: Array<{ name: string; description: string }>;
}

interface OnboardingConfig {
  welcomeScreen: {
    title: string;
    subtitle: string;
    icon: string;
  };
  slides: OnboardingSlide[];
  finalScreen: {
    title: string;
    subtitle: string;
    ctaButtons: {
      primary: { text: string; action: string };
      secondary: { text: string; action: string };
    };
  };
}

interface OnboardingSlidesProps {
  config: OnboardingConfig;
  organizationName: string;
  primaryColor?: string;
  primaryContrast?: string;
}

export function OnboardingSlides({
  config,
  organizationName,
  primaryColor = "#C6FB3B",
  primaryContrast = "#ffffff",
}: OnboardingSlidesProps) {
  const { t } = useTranslation();
  const swiperRef = useRef<SwiperType | null>(null);
  const [_, setActiveIndex] = useState(0);
  const [isLastSlide, setIsLastSlide] = useState(false);
  const history = useHistory();

  const totalSlides = config.slides.length + 2; // +2 for welcome and final screens

  // Helper function to darken a hex color by a percentage
  const darkenColor = (hex: string, percent: number) => {
    // Remove # if present
    hex = hex.replace("#", "");

    // Parse RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Darken by percentage
    const darkenedR = Math.max(0, Math.floor(r * (1 - percent / 100)));
    const darkenedG = Math.max(0, Math.floor(g * (1 - percent / 100)));
    const darkenedB = Math.max(0, Math.floor(b * (1 - percent / 100)));

    // Convert back to hex
    return `#${darkenedR.toString(16).padStart(2, "0")}${darkenedG.toString(16).padStart(2, "0")}${darkenedB.toString(16).padStart(2, "0")}`;
  };

  const primaryColorDark = darkenColor(primaryColor, 15);
  const primaryColorDarker = darkenColor(primaryColor, 25);

  const handleNext = () => {
    swiperRef.current?.slideNext();
  };

  const handleSkip = () => {
    history.push(Routes.AuthSignInPassword());
  };

  const handleAction = (action: string, buttonType: "primary" | "secondary" = "primary") => {
    // Navigate within AuthRouter outlet using useHistory hook
    // Handle custom routing actions (from fallback config)
    if (action === "register") {
      history.push(Routes.AuthRegister());
    } else if (action === "signin") {
      history.push(Routes.AuthSignInPassword());
    }
    // Handle API action types from backend
    else if (action === "close") {
      // "close" action on final screen - route based on button type
      // Primary button = register, Secondary button = sign in
      if (buttonType === "primary") {
        history.push(Routes.AuthRegister());
      } else {
        history.push(Routes.AuthSignInPassword());
      }
    } else if (action === "skip") {
      history.push(Routes.AuthSignInPassword());
    } else if (action === "next") {
      // "next" is typically handled by swiper, but as fallback go to sign in
      history.push(Routes.AuthSignInPassword());
    }
    // Note: "external_link" should be handled separately with a URL
  };

  return (
    <div className="relative flex h-full flex-col bg-surface-primary">
      {/* Close/Skip button */}
      <button
        onClick={handleSkip}
        aria-label={t("common:a11y.skip")}
        className="bg-surface-translucent backdrop-blur absolute right-4 top-8 z-20 flex h-10 w-10 items-center justify-center rounded-full shadow-sm transition-all active:scale-90"
      >
        <IonIcon icon={closeOutline} className="text-xl text-txt-secondary" />
      </button>

      <Swiper
        modules={[Pagination, EffectCreative]}
        pagination={{
          clickable: true,
          bulletClass: "swiper-pagination-bullet !bg-neutral-200",
          bulletActiveClass: "!bg-brand-500",
          enabled: !isLastSlide,
        }}
        effect="creative"
        creativeEffect={{
          prev: {
            translate: ["-20%", 0, -1],
            opacity: 0.5,
          },
          next: {
            translate: ["100%", 0, 0],
          },
        }}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
        onSlideChange={(swiper) => {
          setActiveIndex(swiper.activeIndex);
          setIsLastSlide(swiper.activeIndex === totalSlides - 1);
        }}
        className="h-full w-full"
        speed={400}
        style={
          {
            "--swiper-pagination-bottom": "140px",
            "--swiper-pagination-bullet-size": "8px",
            "--swiper-pagination-bullet-horizontal-gap": "6px",
          } as React.CSSProperties
        }
      >
        {/* Welcome Screen */}
        <SwiperSlide>
          <div className="flex h-full flex-col">
            {/* Hero section with gradient background */}
            <div
              className="relative flex flex-1 flex-col items-center justify-center px-8 pb-32"
              style={{
                background: `linear-gradient(135deg, ${primaryColor}15 0%, ${primaryColor}05 100%)`,
              }}
            >
              <div className="text-7xl mb-8 flex h-32 w-32 items-center justify-center rounded-3xl bg-white shadow-2xl duration-700 animate-in zoom-in">
                {config.welcomeScreen.icon}
              </div>
              <h1 className="mb-4 text-center text-4xl font-bold text-txt-primary delay-150 duration-700 animate-in fade-in slide-in-from-bottom-4">
                {config.welcomeScreen.title}
              </h1>
              <p className="text-center text-lg text-txt-secondary delay-300 duration-700 animate-in fade-in slide-in-from-bottom-4">
                {config.welcomeScreen.subtitle}
              </p>
            </div>
          </div>
        </SwiperSlide>

        {/* Content Slides */}
        {config.slides.map((slide, index) => (
          <SwiperSlide key={index}>
            <div className="flex h-full flex-col">
              {/* Top colored section with icon */}
              <div
                className="flex items-end justify-center px-8 pb-8 pt-24"
                style={{
                  height: "280px",
                  background: `linear-gradient(135deg, ${primaryColor}20 0%, ${primaryColor}10 100())`,
                }}
              >
                <div className="text-6xl flex h-28 w-28 items-center justify-center rounded-3xl bg-white shadow-xl">
                  {slide.icon}
                </div>
              </div>

              {/* White content card that slides up over the colored section */}
              <div className="relative -mt-8 flex-1 overflow-y-auto rounded-t-[32px] bg-white px-6 pb-40 pt-8 shadow-2xl">
                {/* Title */}
                <h2 className="mb-3 text-center text-2xl font-bold text-txt-primary">
                  {slide.title}
                </h2>

                {/* Description */}
                <p className="mb-6 text-center text-base leading-relaxed text-txt-secondary">
                  {slide.description}
                </p>

                {/* Benefits */}
                {slide.benefits && (
                  <div className="space-y-3">
                    {slide.benefits.map((benefit, idx) => (
                      <div
                        key={idx}
                        className="flex items-start gap-3 rounded-2xl border border-bordercolor-primary bg-surface-primary p-4"
                      >
                        <div
                          className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full"
                          style={{ backgroundColor: `${primaryColor}` }}
                        >
                          <IonIcon
                            icon={checkmarkCircle}
                            className="text-lg"
                            style={{ color: primaryContrast }}
                          />
                        </div>
                        <span className="text-sm font-medium text-txt-primary">{benefit}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Roles */}
                {slide.roles && (
                  <div className="space-y-3">
                    {slide.roles.map((role, idx) => (
                      <div
                        key={idx}
                        className="rounded-2xl border border-bordercolor-primary bg-surface-primary p-4"
                      >
                        <h3 className="mb-1 font-semibold text-txt-primary">{role.name}</h3>
                        <p className="text-sm text-txt-secondary">{role.description}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </SwiperSlide>
        ))}

        {/* Final CTA Screen */}
        <SwiperSlide>
          <div className="flex h-full w-full flex-col bg-surface-primary">
            {/* Content area with very subtle gradient */}
            <div
              className="flex flex-1 flex-col items-center justify-center px-8"
              style={{
                background: `linear-gradient(180deg, ${primaryColor}08 0%, transparent 40%, transparent 100%)`,
              }}
            >
              {/* Success Icon */}
              <div className="text-6xl mb-8 flex h-32 w-32 items-center justify-center rounded-3xl bg-white shadow-xl">
                🚀
              </div>

              {/* Title */}
              <h2 className="mb-4 text-center text-3xl font-bold text-txt-primary">
                {config.finalScreen.title}
              </h2>

              {/* Subtitle */}
              <p className="text-center text-lg font-medium text-txt-secondary">
                {config.finalScreen.subtitle}
              </p>
            </div>

            {/* Fixed bottom CTA section with safe area */}
            <div
              className="border-t border-bordercolor-primary bg-white px-6 pb-3 pt-4 shadow-2xl"
              style={{
                paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))",
              }}
            >
              <div className="space-y-3">
                <IonButton
                  onClick={() =>
                    handleAction(config.finalScreen.ctaButtons.primary.action, "primary")
                  }
                  expand="block"
                  className="m-0 h-auto"
                  style={
                    {
                      "--background": primaryColor,
                      "--background-hover": primaryColorDark,
                      "--background-activated": primaryColorDarker,
                      "--background-focused": primaryColorDark,
                      "--color": primaryContrast,
                      "--border-radius": "16px",
                      "--padding-top": "16px",
                      "--padding-bottom": "16px",
                      "--box-shadow":
                        "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
                    } as React.CSSProperties
                  }
                >
                  <div className="flex items-center justify-center gap-2">
                    {config.finalScreen.ctaButtons.primary.text}
                    <IonIcon icon={arrowForward} style={{ color: primaryContrast }} />
                  </div>
                </IonButton>

                <IonButton
                  onClick={() =>
                    handleAction(config.finalScreen.ctaButtons.secondary.action, "secondary")
                  }
                  expand="block"
                  fill="outline"
                  className="m-0 h-auto"
                  style={
                    {
                      "--border-width": "2px",
                      // The theme's own tokens rather than fixed greys, so this button follows the
                      // brand and both appearances like every other surface in the app.
                      "--border-color": "var(--color-bordercolor-primary)",
                      "--color": "var(--color-txt-primary)",
                      "--background-hover": "var(--color-surface-primary)",
                      "--background-activated": "var(--color-surface-secondary)",
                      "--background-focused": "var(--color-surface-primary)",
                      "--border-radius": "16px",
                      "--padding-top": "16px",
                      "--padding-bottom": "16px",
                    } as React.CSSProperties
                  }
                >
                  {config.finalScreen.ctaButtons.secondary.text}
                </IonButton>

                {/* <IonButton
                  onClick={handleSkip}
                  expand="block"
                  fill="clear"
                  className="m-0"
                  style={{
                    '--color': '#6b7280',
                    '--color-hover': '#374151',
                    '--color-activated': '#1f2937',
                    '--background-hover': '#f9fafb',
                    '--background-activated': '#f3f4f6',
                    '--background-focused': '#f9fafb',
                    'fontSize': '14px',
                    'fontWeight': '500',
                  } as React.CSSProperties}
                >
                  Of bekijk eerst beschikbare shifts
                </IonButton> */}
              </div>
            </div>
          </div>
        </SwiperSlide>
      </Swiper>

      {/* Next Button (not on last slide) - positioned above pagination */}
      {!isLastSlide && (
        <div
          className="fixed bottom-0 left-0 right-0 z-10 flex justify-center"
          style={{
            paddingBottom: "calc(2rem + env(safe-area-inset-bottom))",
          }}
        >
          <button
            onClick={handleNext}
            aria-label={t("common:a11y.next")}
            className="flex h-14 w-14 items-center justify-center rounded-full shadow-2xl transition-all active:scale-90"
            style={{ backgroundColor: primaryColor }}
          >
            <IonIcon icon={arrowForward} className="text-2xl" style={{ color: primaryContrast }} />
          </button>
        </div>
      )}
    </div>
  );
}
